// import { apiSlice } from '../api/apiSlice'
// import 

import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const STOCK_BASE = "/stocks";
const buildUrl = (endpoint) => STOCK_BASE + endpoint;

// Handles the response of an export endpoint: a JSON body with a blobUrl downloads the file at
// once, a 202 means the worker is still generating it and the user is notified later.
const exportResponseHandler = (defaultFileName) => async (response) => {
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();

        if (jsonData.blobUrl) {
            const a = document.createElement('a');
            a.href = jsonData.blobUrl;
            a.download = jsonData.fileName || defaultFileName;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            a.remove();

            return { success: true, message: jsonData.message, immediate: true };
        }

        return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = defaultFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, immediate: true };
};

const stockApiSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getStock: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'Stock' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getStock', 'Stock')
        }),
        getAllStock: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/all?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'Stock' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getStock', 'Stock')
        }),
        getStockForProduct: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: 'StockForProduct'}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getStockForProduct', 'StockForProduct')
        }),
        getStockForProductForEcheck: builder.query({
            query: (body) => ({
                url: buildUrl(`?page=1&size=200`),
                method: 'POST',
                body: body,
            }),
            providesTags: (result, error, arg) => [{ type: 'StockForProduct', id: arg?.productId }],
        }),
        getStockById: builder.query({
            query: stockId => buildUrl(`/${stockId}`),
            providesTags: (result, error, arg) => [{ type: 'Stock', id: arg }]
        }),
        getStockMutations: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/mutations?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'StockMutations' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getStockMutations', 'StockMutations')
        }),
        // ABC analysis of the picks per product over the previous calendar month
        getAbcAnalysis: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/abcanalysis?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'StockAbcAnalysis' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getAbcAnalysis', 'StockAbcAnalysis')
        }),
        getStockMutationsChart: builder.query({
            query: (body) => ({
                url: buildUrl(`/mutations/between/chart`),
                method: 'POST',
                body: body,
            }),
            providesTags: () => [{ type: 'StockMutationsChart' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getStockMutationsChart', 'StockMutationsChart')
        }),
        createStock: builder.mutation({
            query: (stockObject) => ({
                url: buildUrl(`/new`),
                method: 'POST',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}, { type: 'Products', id: arg.productId }, { type: 'StockForProduct'}, { type: 'ProductsSingle'}, { type: 'Orders' }, { type: 'OrdersSingle' }, { type: 'OrderLines' }],
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}, { type: 'Products', id: arg.productId }, { type: 'StockForProduct'}, { type: 'ProductsSingle'}, { type: 'Orders' }, { type: 'OrdersSingle' }, { type: 'OrderLines' }],
        }),
        moveStock: builder.mutation({
            query: (stockObject) => ({
                url: buildUrl(`/move`),
                method: 'POST',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}, { type: 'Products', id: arg.productId }, { type: 'StockForProduct'}, { type: 'ProductsSingle'}, { type: 'Orders' }, { type: 'OrdersSingle' }, { type: 'OrderLines' }, { type: 'OrderTotals' }],
        }),
        updateStock: builder.mutation({
            query: (stockObject) => ({
                url: buildUrl(`/${stockObject.id}`),
                method: 'PUT',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}, { type: 'Products', id: arg.productId }, { type: 'StockForProduct'}, { type: 'ProductsSingle'}],
        }),
        updateStockQuantity: builder.mutation({
            query: (stockObject) => ({
                url: buildUrl(`/${stockObject.id}/quantity`),
                method: 'PUT',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}, { type: 'Products', id: arg.productId }, { type: 'StockForProduct'}, { type: 'ProductsSingle'}, { type: 'Orders' }, { type: 'OrdersSingle' }, { type: 'OrderLines' }, { type: 'OrderTotals' }],
        }),
        bulkUpdateStock: builder.mutation({
            query: stockObject => ({
                url: buildUrl(`/bulk`),
                method: 'PUT',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}],
        }),
        bulkUpdateStock: builder.mutation({
            query: stockObject => ({
                url: buildUrl(`/bulk`),
                method: 'PUT',
                body: stockObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Stock'}],
        }),
        exportPickingStock: builder.mutation({
            query: filterParams => {
                return {
                  url: buildUrl(`/export/pickingstock`),
                  method: 'POST',
                  body: filterParams,
                  responseHandler: async (response) => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }

                        const contentType = response.headers.get('content-type');

                        // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                        if (contentType && contentType.includes('application/json')) {
                            const jsonData = await response.json();

                            // If immediate download is ready (status 200 with blobUrl)
                            if (jsonData.blobUrl) {
                                // For storage URLs or signed URLs, just trigger direct download
                                // No need to fetch again - the URL is already accessible
                                const a = document.createElement('a');
                                a.href = jsonData.blobUrl;
                                a.download = jsonData.fileName || `voorraad_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                                a.target = '_blank'; // Open in new tab as fallback
                                document.body.appendChild(a);
                                a.click();
                                a.remove();

                                return { success: true, message: jsonData.message, immediate: true };
                            }

                            // Background processing (status 202)
                            return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
                        }

                        // Legacy support: Direct blob response (for backwards compatibility)
                        const blob = await response.blob();

                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `voorraad_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);

                        return { success: true, immediate: true };
                    },
                };
              },
        }),
        // Excel export of the ABC analysis with the relation and warehouse filters
        exportAbcAnalysis: builder.mutation({
            query: body => ({
                url: buildUrl(`/abcanalysis/export`),
                method: 'POST',
                body: body,
                responseHandler: exportResponseHandler(`abc_analyse_${new Date().toISOString().split('T')[0]}.xlsx`),
            }),
        }),
        exportStockStatus: builder.mutation({
            query: body => {
                return {
                    url: buildUrl(`/stockstatus/export`),
                    method: 'POST',
                    body: body,
                    responseHandler: async (response) => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }

                        const contentType = response.headers.get('content-type');

                        // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                        if (contentType && contentType.includes('application/json')) {
                            const jsonData = await response.json();

                            // If immediate download is ready (status 200 with blobUrl)
                            if (jsonData.blobUrl) {
                                // For storage URLs, just trigger direct download
                                const a = document.createElement('a');
                                a.href = jsonData.blobUrl;
                                a.download = jsonData.fileName || `Voorraadstand ${body.targetDate}.xlsx`;
                                a.target = '_blank'; // Open in new tab as fallback
                                document.body.appendChild(a);
                                a.click();
                                a.remove();

                                return { success: true, message: jsonData.message, immediate: true };
                            }

                            // Background processing (status 202)
                            return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
                        }

                        // Legacy support: Direct blob response (for backwards compatibility)
                        const blob = await response.blob();

                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Voorraadstand ${body.targetDate}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);

                        return { success: true, immediate: true };
                    },
                };
            },
        }),
    })
});

export const { 
    useGetStockQuery,
    useGetAllStockQuery,
    useGetStockForProductQuery, 
    useGetStockByIdQuery,
    useGetStockMutationsQuery,
    useGetAbcAnalysisQuery,
    useExportAbcAnalysisMutation,
    useExportPickingStockMutation,
    useUpdateStockQuantityMutation,
    useUpdateStockMutation,
    useGetStockForProductForEcheckQuery,
    useCreateStockMutation,
    useMoveStockMutation,
    useGetStockMutationsChartQuery,
    useExportStockStatusMutation,
    useBulkUpdateStockMutation
 } = stockApiSlice;
